import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';

/**
 * Maps the current route to the TanStack Query key prefix used by that
 * screen's data hooks. Keep in sync with the queryKey values in
 * `src/hooks/api/*` — adding a new tab means adding its key here too.
 */
function refreshKeyForPath(pathname: string): readonly string[] | null {
  if (pathname.startsWith('/today')) return ['dashboard'];
  if (pathname.startsWith('/guests')) return ['guests'];
  if (pathname.startsWith('/meals')) return ['meals'];
  if (pathname.startsWith('/occupancy')) return ['occupancy'];
  if (pathname.startsWith('/approvals')) return ['approvals'];
  return null;
}

/**
 * Shared refresh logic used by both the header refresh icon and the
 * pull-to-refresh gesture. Returns `available: false` on routes with no
 * data hooks (login, property-picker) so the caller can hide its trigger.
 */
export function useCurrentRouteRefresh() {
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const key = refreshKeyForPath(pathname);

  // Hook must be called unconditionally — sentinel key keeps the count at 0
  // when the current route has no data queries.
  const isFetching = useIsFetching({
    queryKey: key ?? ['__no_refresh__'],
    exact: false,
  });

  const refresh = useCallback(() => {
    if (key) void queryClient.invalidateQueries({ queryKey: key });
  }, [key, queryClient]);

  return {
    available: key !== null,
    isRefreshing: isFetching > 0,
    refresh,
  };
}
